import {
  useGetCompetitorVideosQuery,
  useGetCompetitorVideosByBrandQuery,
  useGetVideosByPersonaQuery,
  useGetPersonaQuery,
  useGetRedditCommentsQuery,
  useGetOrganicAdsQuery,
} from "@/redux/feature/product/productApi";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import ProductDetailsModal from "../productDetailsModal/ProductDetailsModal";
import Modal from "@/components/ui/Modal";
import TopCompetitorIcon from "@/assets/icons/TopCompetitor.svg";
import OrganicAdsIcon from "@/assets/icons/OrganicAds.svg";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";
import EvidenceCommentsDisplay from "@/pages/ProductStructure/components/EvidenceCommentsDisplay";
import SelectModal from "@/pages/ProductStructure/components/SelectModal/SelectModal";
import Calender from "../calender/Calender";
import Trends from "../trends/Trends";
import AudienceSelection from "./AudienceSelection";
import RedditComments from "./RedditComments";
import VideoSectionWrapper from "./VideoSectionWrapper";
import { usePagination } from "@/hooks/usePagination";

const POLLING_INTERVAL = 10000;

const CompetitorVideos = ({
  selectedBrand,
  selectedAudience,
  setSelectedAudience,
  onAudienceChange,
  setSelectedBrand,
}: {
  selectedBrand: string | null;
  selectedAudience: string | null;
  setSelectedAudience: (audience: string | null) => void;
  setSelectedBrand: (audience: string | null) => void;
  onAudienceChange: (audience: string | null) => void;
}) => {
  const { brandName, productName, country, productId } = useParams();

  // Local States
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [selectedCommentsThread, setSelectedCommentsThread] =
    useState<any>(null);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [pollingStatus, setPollingStatus] = useState({
    organicAds: true,
    competitor: true,
    audiences: true,
    redditComments: true,
  });

  // Pagination Hooks
  const organicAdsPagination = usePagination();
  const competitorPagination = usePagination();
  const brandPagination = usePagination();
  const personaPagination = usePagination();

  // API Queries
  const { data: audiencesData = { data: [], meta: { isProcessing: false } } } =
    useGetPersonaQuery(
      { productId: productId! },
      { pollingInterval: pollingStatus.audiences ? POLLING_INTERVAL : 0 }
    );

  const {
    data: videosByPersona = { competitorAds: [], organicAds: [], meta: {} },
  } = useGetVideosByPersonaQuery(
    {
      productId: productId!,
      persona: selectedAudience as string,
      page: personaPagination.page,
      limit: personaPagination.limit,
    },
    { skip: !selectedAudience }
  );

  const {
    data: organicAds = {
      data: [],
      meta: { isProcessing: false, totalPages: 1 },
    },
    isFetching: isOrganicAdsFetching,
    isLoading: isOrganicAdsLoading,
  } = useGetOrganicAdsQuery(
    {
      productId: productId!,
      page: organicAdsPagination.page,
      limit: organicAdsPagination.limit,
    },
    {
      pollingInterval: pollingStatus.organicAds ? POLLING_INTERVAL : 0,
      skip: !!selectedAudience || !!selectedBrand,
    }
  );

  const {
    data: competitorResponse = {
      data: [],
      meta: { isProcessing: false, totalPages: 1 },
    },
    isFetching: isCompetitorFetching,
    isLoading: isCompetitorLoading,
  } = useGetCompetitorVideosQuery(
    {
      productId: productId!,
      page: competitorPagination.page,
      limit: competitorPagination.limit,
    },
    {
      pollingInterval: pollingStatus.competitor ? POLLING_INTERVAL : 0,
      skip: !!selectedAudience || !!selectedBrand,
    }
  );

  const {
    data: brandFilteredVideos = {
      competitorAds: [],
      organicAds: [],
      meta: { totalPages: 1 },
    },
    isFetching: isBrandFilteredFetching,
  } = useGetCompetitorVideosByBrandQuery(
    {
      comp_name: selectedBrand!,
      productId: productId!,
      page: brandPagination.page,
      limit: brandPagination.limit,
    },
    { skip: !selectedBrand }
  );

  const {
    data: redditComments = { data: [], meta: { isProcessing: false } },
    isLoading: isRedditCommentsLoading,
  } = useGetRedditCommentsQuery(
    { productId: productId! },
    {
      pollingInterval: pollingStatus.redditComments ? POLLING_INTERVAL : 0,
      skip: !brandName && !country && !productName,
    }
  );

  // Polling stop
  useEffect(() => {
    setPollingStatus((prev) => ({
      ...prev,
      audiences: audiencesData.meta?.isProcessing !== false,
      competitor: competitorResponse.meta?.isProcessing !== false,
      organicAds: organicAds.meta?.isProcessing !== false,
      redditComments: redditComments.meta?.isProcessing !== false,
    }));
  }, [
    audiencesData.meta,
    competitorResponse.meta,
    organicAds.meta,
    redditComments.meta,
  ]);

  // Initial load complete
  useEffect(() => {
    if (!initialLoadComplete) {
      const hasData =
        (selectedAudience && videosByPersona?.competitorAds?.length) ||
        (selectedBrand && brandFilteredVideos?.competitorAds?.length) ||
        (!selectedAudience && !selectedBrand && competitorResponse.data.length);

      if (hasData) setInitialLoadComplete(true);
    }
  }, [
    videosByPersona,
    competitorResponse.data,
    brandFilteredVideos,
    selectedAudience,
    selectedBrand,
    initialLoadComplete,
  ]);

  // Audience selection
  const handleAudienceSelect = useCallback(
    (audience: string) => {
      setSelectedBrand("");
      const newAudience = selectedAudience === audience ? null : audience;
      setSelectedAudience(newAudience);
      onAudienceChange(newAudience);
      personaPagination.resetPage();
    },
    [selectedAudience, onAudienceChange, setSelectedAudience, setSelectedBrand]
  );

  const handleOpenCommentsModal = useCallback((item: any) => {
    setIsCommentsModalOpen(true);
    setSelectedCommentsThread(item);
  }, []);

  // Loading states
  const competitorsAdsLoading =
    (competitorResponse?.data?.length ?? 0) === 0 &&
    (isCompetitorLoading || isCompetitorFetching);

  const organicAdsLoading =
    (videosByPersona?.organicAds?.length ?? 0) === 0 &&
    (isOrganicAdsFetching || isOrganicAdsLoading);

  const redditCommentsLoading =
    (redditComments?.data?.length ?? 0) === 0 &&
    (isRedditCommentsLoading || redditComments?.meta?.isProcessing);

  const filteredAudiences = useMemo(
    () =>
      audiencesData.data?.map((item: { name: string }) => item?.name ?? item) ??
      [],
    [audiencesData.data]
  );

  useEffect(() => {
    competitorPagination.resetPage();
    brandPagination.resetPage();
    organicAdsPagination.resetPage();
  }, [brandName]);

  return (
    <div className="w-full">
      <AudienceSelection
        audiencesData={audiencesData}
        filteredAudiences={filteredAudiences}
        handleAudienceSelect={handleAudienceSelect}
        selectedAudience={selectedAudience}
      />

      {/* Competitor Ads */}
      <VideoSectionWrapper
        title="Top Competitor Ads"
        icon={<TopCompetitorIcon />}
        videos={
          selectedAudience
            ? videosByPersona?.competitorAds || []
            : selectedBrand
            ? brandFilteredVideos?.competitorAds || []
            : competitorResponse.data || []
        }
        isLoading={competitorsAdsLoading && !initialLoadComplete}
        currentlyPlaying={currentlyPlaying}
        setCurrentlyPlaying={setCurrentlyPlaying}
        setIsDetailsModalOpen={setIsDetailsModalOpen}
        setSelectedVideo={setSelectedVideo}
        pagination={{
          currentPage: competitorPagination.page,
          totalPages: competitorResponse.meta.totalPages,
          setPage: competitorPagination.setPage,
        }}
        isProcessing={competitorResponse.meta?.isProcessing || false}
      />

      {/* Organic Ads */}
      <VideoSectionWrapper
        title="Top Competitor Organic Videos"
        icon={<OrganicAdsIcon />}
        videos={
          selectedAudience
            ? videosByPersona?.organicAds || []
            : selectedBrand
            ? brandFilteredVideos?.organicAds || []
            : organicAds.data || []
        }
        isLoading={organicAdsLoading && !initialLoadComplete}
        currentlyPlaying={currentlyPlaying}
        setCurrentlyPlaying={setCurrentlyPlaying}
        setIsDetailsModalOpen={setIsDetailsModalOpen}
        setSelectedVideo={setSelectedVideo}
        pagination={{
          currentPage: organicAdsPagination.page,
          totalPages: organicAds.meta.totalPages,
          setPage: organicAdsPagination.setPage,
        }}
        isProcessing={organicAds.meta?.isProcessing || false}
      />

      {/* Trends & Calendar */}
      <Trends
        brand={brandName as string}
        product={productName as string}
        country={country as string}
      />
      <Calender
        brandName={brandName as string}
        country={country as string}
        product={productName as string}
        productId={productId as string}
      />

      {/* Reddit Comments */}
      <RedditComments
        handleOpenCommentsModal={handleOpenCommentsModal}
        redditComments={redditComments}
        redditCommentsLoading={redditCommentsLoading}
        setShowAllComments={setShowAllComments}
        showAllComments={showAllComments}
      />

      {/* Modals */}
      <Modal
        padding="12px"
        width="750px"
        isOpen={isDetailsModalOpen}
        setOpen={setIsDetailsModalOpen}
      >
        <ProductDetailsModal
          showGenerateScript={true}
          video={selectedVideo as VideoData}
          setIsDetailsModalOpen={setIsDetailsModalOpen}
          setIsSelectModalOpen={setIsSelectModalOpen}
        />
      </Modal>

      <Modal
        padding="12px"
        width="min(721px, 95vw)"
        isOpen={isCommentsModalOpen}
        setOpen={setIsCommentsModalOpen}
      >
        <EvidenceCommentsDisplay
          evidenceComments={selectedCommentsThread?.comments}
        />
      </Modal>

      <Modal
        isOpen={isSelectModalOpen}
        setOpen={setIsSelectModalOpen}
        width="min(900px, 95vw)"
      >
        <SelectModal
          isOpen={isSelectModalOpen}
          setOpen={setIsSelectModalOpen}
        />
      </Modal>
    </div>
  );
};

export default CompetitorVideos;
